import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DailyRollupInput } from '@ectopics/shared/schemas';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SeveritySlider } from '@/components/ui/severity-slider';
import { Textarea } from '@/components/ui/textarea';
import { KindIcon } from '@/components/kind-icon';
import { api } from '@/lib/api';
import { detectTimeZone, localDateInTz } from '@/lib/tz';

export const Route = createFileRoute('/rollup')({
  component: RollupPage,
});

function RollupPage() {
  const tz = React.useMemo(detectTimeZone, []);
  const today = React.useMemo(() => localDateInTz(new Date(), tz), [tz]);
  const [dateOffset, setDateOffset] = React.useState(0);
  const activeDate = React.useMemo(() => {
    if (dateOffset === 0) return today;
    const d = new Date();
    d.setDate(d.getDate() + dateOffset);
    return localDateInTz(d, tz);
  }, [dateOffset, today, tz]);

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h1 className="text-2xl font-semibold tracking-tight">Daily rollup</h1>
        <p className="text-sm text-muted-foreground">
          Five sliders + a quick note. One rollup per day; resubmitting overwrites.
        </p>
      </section>

      <div className="flex items-center justify-between">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setDateOffset((o) => Math.max(o - 1, -6))}
          disabled={dateOffset <= -6}
        >
          ← Earlier
        </Button>
        <div className="text-sm font-medium tabular-nums">
          {activeDate}
          {dateOffset === 0 && <span className="ml-2 text-muted-foreground">(today)</span>}
          {dateOffset === -1 && <span className="ml-2 text-muted-foreground">(yesterday)</span>}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setDateOffset((o) => Math.min(o + 1, 0))}
          disabled={dateOffset >= 0}
        >
          Later →
        </Button>
      </div>

      <RollupForm key={activeDate} date={activeDate} tz={tz} />
    </div>
  );
}

type RollupFormProps = {
  date: string;
  tz: string;
};

function RollupForm({ date, tz }: RollupFormProps) {
  const qc = useQueryClient();
  const existing = useQuery({
    queryKey: ['rollup', date],
    queryFn: () => api.getRollup(date),
    staleTime: 60_000,
  });

  const [overall, setOverall] = React.useState(3);
  const [ectopic, setEctopic] = React.useState(3);
  const [reflux, setReflux] = React.useState(3);
  const [ibs, setIbs] = React.useState(3);
  const [pain, setPain] = React.useState(3);
  const [note, setNote] = React.useState('');
  const [hydrated, setHydrated] = React.useState(false);

  React.useEffect(() => {
    if (hydrated) return;
    if (existing.isPending) return;
    if (existing.data) {
      setOverall(existing.data.overall_score);
      setEctopic(existing.data.ectopic_score);
      setReflux(existing.data.reflux_score);
      setIbs(existing.data.ibs_score);
      setPain(existing.data.pain_score);
      setNote(existing.data.note ?? '');
    }
    setHydrated(true);
  }, [existing.isPending, existing.data, hydrated]);

  const submit = useMutation({
    mutationFn: async () => {
      const input: DailyRollupInput = {
        local_date: date,
        tz,
        overall_score: overall,
        ectopic_score: ectopic,
        reflux_score: reflux,
        ibs_score: ibs,
        pain_score: pain,
        note: note.trim() || undefined,
      };
      return api.submitRollup(input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rollup', date] });
      void qc.invalidateQueries({ queryKey: ['rollups'] });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>How did the day feel?</CardTitle>
        <CardDescription>
          1 = great day, 5 = terrible day. {existing.data && 'Editing an existing rollup.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <SeveritySlider label="Overall" value={overall} onChange={setOverall} />
        <SeveritySlider
          label={
            <>
              <KindIcon kind="ectopic" className="h-4 w-4 text-rose-500" />
              Ectopic
            </>
          }
          value={ectopic}
          onChange={setEctopic}
        />
        <SeveritySlider
          label={
            <>
              <KindIcon kind="reflux" className="h-4 w-4 text-amber-500" />
              Reflux
            </>
          }
          value={reflux}
          onChange={setReflux}
        />
        <SeveritySlider
          label={
            <>
              <KindIcon kind="ibs" className="h-4 w-4 text-emerald-500" />
              IBS
            </>
          }
          value={ibs}
          onChange={setIbs}
        />
        <SeveritySlider
          label={
            <>
              <KindIcon kind="pain" className="h-4 w-4 text-violet-500" />
              Pain
            </>
          }
          value={pain}
          onChange={setPain}
        />

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium" htmlFor="rollup-note">
            Note (optional)
          </label>
          <Textarea
            id="rollup-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What stood out today?"
            maxLength={2000}
          />
        </div>

        {submit.data?.status === 'sent' && (
          <p className="text-sm text-emerald-600">Saved.</p>
        )}
        {submit.data?.status === 'queued' && (
          <p className="text-sm text-amber-600">
            Saved offline. Will sync when reconnected.
          </p>
        )}
        {submit.data?.status === 'rejected' && (
          <p className="text-sm text-destructive">Rejected: {submit.data.error}</p>
        )}
        {submit.error && (
          <p className="text-sm text-destructive">{(submit.error as Error).message}</p>
        )}

        <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
          {submit.isPending
            ? 'Saving…'
            : existing.data
              ? 'Update rollup'
              : 'Save rollup'}
        </Button>

        {existing.data && (
          <p className="text-xs text-muted-foreground">
            Last submitted{' '}
            {new Date(existing.data.submitted_at).toLocaleString([], {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}{' '}
            ({existing.data.tz}).
          </p>
        )}
      </CardContent>
    </Card>
  );
}
